import { useEffect, useState } from 'react';
import { api, BladApi, type Sesja, type Ustawienia } from '../api';
import { NaglowekWidoku } from '../components/Shell';
import { Karta, Komunikat } from '../components/ui';

/**
 * Ustawienia firmy.
 *
 * Ekran powstał z konkretnej sprzeczności, a nie „na zapas": `docs/model-danych.md`
 * §3.2 nazywa zdjęcie ogólne i tabliczkę **obowiązkowymi** przy zakładaniu karty,
 * a zasada 2 mówi, że poza trzema polami nic nie może blokować zapisu. Kod nie ma
 * prawa rozstrzygać tego za firmę — rozstrzyga przełącznik.
 *
 * Każde ustawienie, które tu dojdzie, musi przejść ten sam test: czy dwie firmy
 * serwisowe mogą chcieć inaczej? Jeśli nie — to nie jest ustawienie, tylko decyzja
 * projektowa i ma zostać w kodzie.
 */
export function SettingsView({ sesja }: { sesja: Sesja }) {
  const mozeZmieniac = sesja.permissions.includes('settings.write');

  const [ustawienia, setUstawienia] = useState<Ustawienia | null>(null);
  const [blad, setBlad] = useState<string | null>(null);
  const [zapis, setZapis] = useState(false);

  useEffect(() => {
    api
      .ustawienia()
      .then(setUstawienia)
      .catch(() => setBlad('Nie udało się wczytać ustawień.'));
  }, []);

  async function przelacz(zmiana: Partial<Ustawienia>): Promise<void> {
    if (!mozeZmieniac || zapis) return;

    setZapis(true);
    setBlad(null);
    // Zapisujemy od razu po kliknięciu. Osobny przycisk „Zapisz" przy jednym
    // przełączniku byłby pytaniem, na które i tak zawsze pada ta sama odpowiedź.
    try {
      setUstawienia(await api.zapiszUstawienia(zmiana));
    } catch (error: unknown) {
      setBlad(error instanceof BladApi ? error.message : 'Nie udało się zapisać ustawienia.');
    } finally {
      setZapis(false);
    }
  }

  return (
    <>
      <NaglowekWidoku
        tytul="Ustawienia"
        podtytul={mozeZmieniac ? 'Zmiany zapisują się od razu' : 'Podgląd — zmiany robi administrator'}
      />

      {blad && (
        <div className="mb-14">
          <Komunikat tresc={blad} />
        </div>
      )}

      <div className="max-w-[760px]">
        <Karta tytul="Przyjęcie maszyny na serwis">
          <Przelacznik
            etykieta="Zdjęcia wymagane przy zakładaniu karty"
            // Opis mówi o stanie faktycznym, nie o obu naraz — zdanie „Wyłączone…"
            // przy włączonym przełączniku czyta się jak informacja, a jest zagadką.
            opis={
              ustawienia?.zdjeciaWymaganePrzyKarcie
                ? 'Bez zdjęcia ogólnego i tabliczki znamionowej nie da się założyć karty.'
                : 'Zdjęcia są zalecane, ale nie blokują założenia karty — można je dorobić przy kolejnej wizycie.'
            }
            wlaczony={ustawienia?.zdjeciaWymaganePrzyKarcie ?? false}
            dostepny={mozeZmieniac && ustawienia !== null && !zapis}
            onZmiana={(wartosc) => void przelacz({ zdjeciaWymaganePrzyKarcie: wartosc })}
          />
        </Karta>
      </div>
    </>
  );
}

/**
 * Przełącznik. Kolor marki niesie stan „włączone" — to oznaczenie wyboru, jedyne
 * poza akcją główną zastosowanie koloru w tej aplikacji (CLAUDE.md §5).
 */
function Przelacznik({
  etykieta,
  opis,
  wlaczony,
  dostepny,
  onZmiana,
}: {
  etykieta: string;
  opis: string;
  wlaczony: boolean;
  dostepny: boolean;
  onZmiana: (wartosc: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={wlaczony}
      disabled={!dostepny}
      onClick={() => onZmiana(!wlaczony)}
      className={`flex w-full items-start gap-13 border bg-surface px-13 py-13 text-left disabled:opacity-60 ${
        wlaczony ? 'border-brand-500' : 'border-n-300 hover:bg-n-50'
      }`}
    >
      <span
        aria-hidden="true"
        className={`mt-4 flex h-[22px] w-[38px] shrink-0 items-center border p-4 ${
          wlaczony ? 'border-brand-500 bg-brand-500' : 'border-n-300 bg-n-50'
        }`}
      >
        <span
          className={`block h-[12px] w-[12px] bg-surface ${wlaczony ? 'ml-auto' : ''}`}
        />
      </span>

      <span className="min-w-0">
        <span className="t-body-strong block text-n-900">{etykieta}</span>
        <span className="t-small mt-4 block text-n-600">{opis}</span>
      </span>

      <span className={`t-mono-micro ml-auto shrink-0 ${wlaczony ? 'text-brand-700' : 'text-n-500'}`}>
        {wlaczony ? 'WŁĄCZONE' : 'WYŁĄCZONE'}
      </span>
    </button>
  );
}
